import {
  Box,
  Button,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Particles, { ParticlesProvider } from "@tsparticles/react";
import { loadSlim } from "@tsparticles/slim";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import FadeInImage from "./FadeInImage";

export default function CarruselSimple() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const particlesInit = useCallback(async (engine: any) => {
    await loadSlim(engine);
  }, []);

  return (
    <ParticlesProvider init={particlesInit}>
      <Box
        component="section"
        aria-label={t("alt.our_team") || "Our Team"}
        sx={{
          position: "relative",
          width: "100vw",
          marginLeft: "calc(50% - 50vw)",
          overflow: "hidden",
          minHeight: {
            xs: "85vh",
            md: "92vh",
          },
          display: "flex",
          alignItems: "center",
        }}
      >
        {/* Imagen fondo */}
        <FadeInImage
          src="/enterprise_it_team.webp"
          alt={t("alt.our_team") || "Our Team"}
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: {
              xs: "calc(100% + clamp(0px, calc((430px - 100vw) * 0.05), 8px))",
              md: "100%",
            },
            top: {
              xs: "clamp(0px, calc((430px - 100vw) * 0.06), 8px)",
              md: 0,
            },
            '& img': {
              objectFit: "cover",
              objectPosition: {
                xs: "center top",
                md: "center center",
              },
            }
          }}
        />

        {/* Overlay oscuro */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 0,
            backgroundColor: "rgba(10,17,40,0.70)",
          }}
        />

        {/* Patrón de puntos */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 1,
            pointerEvents: "none",
            opacity: 0.12,
            backgroundImage:
              "radial-gradient(rgba(6,182,212,.4) 1.5px, transparent 1.5px)",
            backgroundSize: "28px 28px",
          }}
        />

        {/* Partículas */}
        <Particles
          id="hero-particles"
          //particlesInit={particlesInit}
          //particlesLoaded={particlesLoaded}
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 2,
          }}
          options={{
            fullScreen: {
              enable: false,
            },
            fpsLimit: 60,
            particles: {
              number: {
                value: isMobile ? 35 : isTablet ? 55 : 80,
              },
              color: {
                value: "#00E5FF",
              },
              links: {
                enable: true,
                color: "#00E5FF",
                distance: isMobile ? 150 : 250, // Aumenta la distancia
                opacity: 0.5,
                width: 1.2,
              },
              move: {
                enable: true,
                speed: isMobile ? 0.1 : 0.15, // Reduce la velocidad
                direction: "none",
                random: false,
                straight: false,
                outModes: {
                  default: "out",
                },
              },
              size: {
                value: {
                  min: 2,
                  max: 4,
                },
              },
              opacity: {
                value: {
                  min: 0.4,
                  max: 0.6,
                },
              },
            },
            detectRetina: true,
          }}
        />

        {/* Gradiente lateral */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 3,
            pointerEvents: "none",
            background:
              "linear-gradient(90deg,#0A1128 0%,rgba(10,17,40,.75) 45%,rgba(10,17,40,0) 100%)",
          }}
        />

        {/* Glow inferior */}
        <Box
          sx={{
            position: "absolute",
            bottom: -250,
            right: -250,
            width: 600,
            height: 600,
            borderRadius: "50%",
            background: "rgba(6,182,212,.12)",
            filter: "blur(140px)",
            zIndex: 3,
          }}
        />

        {/* Glow superior */}
        <Box
          sx={{
            position: "absolute",
            top: 50,
            left: "25%",
            width: 400,
            height: 400,
            borderRadius: "50%",
            background: "rgba(37,99,235,.08)",
            filter: "blur(120px)",
            zIndex: 3,
          }}
        />

        {/* Contenido */}
        <Box
          sx={{
            position: "relative",
            zIndex: 5,
            width: "100%",
            maxWidth: "1400px",
            mx: "auto",
            px: {
              xs: 3,
              sm: 5,
              md: 8,
              lg: 10,
            },
            "@keyframes fadeUp": {
              from: {
                opacity: 0,
                transform: "translateY(30px)",
              },
              to: {
                opacity: 1,
                transform: "translateY(0)",
              },
            },
            animation: "fadeUp .9s ease-out",
          }}
        >
          <Box
            sx={{
              maxWidth: {
                xs: "100%",
                sm: "90%",
                md: 850,
                lg: 1000,
              },
              display: "flex",
              flexDirection: "column",
              gap: {
                xs: 3,
                md: 4,
              },
                mt: {
                xs: "-12rem",
                md: 0,
              },
            }}
          >
            {/* Título */}
            <Typography
              sx={{
                color: "#fff",
                fontWeight: 800,
                lineHeight: 1.1,
                letterSpacing: "-0.03em",
                textShadow: "0 8px 24px rgba(0,0,0,.35)",
                fontSize: {
                  xs: "2rem",
                  sm: "3rem",
                  md: "4rem",
                  lg: "4.75rem",
                },
              }}
            >
              {t("carrusel_simple.title")}
            </Typography>

            {/* Subtítulo */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 3,
                maxWidth: "850px",
              }}
            >
              <Box
                sx={{
                  width: "6px",
                  alignSelf: "stretch",
                  minHeight: {
                    xs: 60,
                    md: 80,
                  },
                  borderRadius: "999px",
                  bgcolor: "#06b6d4",
                  flexShrink: 0,
                }}
              />

              <Typography
                sx={{
                  color: "rgba(241,245,249,.92)",
                  fontWeight: 400,
                  lineHeight: 1.6,
                  fontSize: {
                    xs: "1rem",
                    sm: "1.2rem",
                    md: "1.45rem",
                    lg: "1.65rem",
                  },
                }}
              >
                {t("carrusel_simple.description")}
              </Typography>
            </Box>

            {/* CTA */}
            <Box>
              <Button
                variant="contained"
                onClick={() => navigate("/servicios")}
                sx={{
                  px: {
                    xs: 4,
                    md: 5,
                  },
                  py: {
                    xs: 1.5,
                    md: 1.8,
                  },
                  bgcolor: "#06b6d4",
                  color: "#0A1128",
                  fontWeight: 800,
                  fontSize: "0.95rem",
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  borderRadius: "10px",
                  boxShadow: "0 10px 35px rgba(6,182,212,.30)",
                  transition: ".3s",
                  "&:hover": {
                    bgcolor: "#22d3ee",
                    transform: "translateY(-2px)",
                    boxShadow: "0 15px 45px rgba(6,182,212,.45)",
                  },
                }}
              >
                {t("carrusel_simple.cta", "Descubrir soluciones")}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </ParticlesProvider>
  );
}
